import { useEffect, useCallback, useRef } from 'react';
import useAuthStore from '../stores/authStore';
import useUIStore from '../stores/uiStore';
import guestService from '../services/guestService';
import { LoginRequest, RegisterRequest, ResetPasswordRequest } from '../services/auth';

export const useAuth = () => {
  const authStore = useAuthStore();
  const uiStore = useUIStore();
  const refreshIntervalRef = useRef<number | null>(null);

  // Restore session on mount
  useEffect(() => {
    if (!authStore.isSessionRestored) {
      authStore.restoreSession().catch((error) => {
        console.error('Failed to restore session:', error);
        uiStore.setError(error, 'Failed to restore session');
      });
    }
  }, [authStore.isSessionRestored]);

  // Set up automatic token refresh
  useEffect(() => {
    if (authStore.isAuthenticated) {
      // Use different refresh intervals based on guest status
      // Guest tokens: Refresh every 23 hours (1 hour before 24-hour expiration)
      // Regular tokens: Refresh every 50 minutes (5 minutes before 60-minute expiration)
      const refreshInterval = authStore.isGuest 
        ? 23 * 60 * 60 * 1000  // 23 hours for guest tokens
        : 50 * 60 * 1000;      // 50 minutes for regular tokens
      
      refreshIntervalRef.current = setInterval(async () => {
        try {
          await authStore.ensureValidToken();
        } catch (error) {
          console.error('Automatic token refresh failed:', error);
          // The auth store will handle logout on failure
        }
      }, refreshInterval);
    } else {
      // Clear interval when not authenticated
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [authStore.isAuthenticated, authStore.isGuest]);

  // Handle authentication events
  useEffect(() => {
    let isHandlingLogout = false;
    
    const handleLogout = () => {
      // Prevent multiple simultaneous logout handlers
      if (isHandlingLogout) {
        return;
      }
      // Don't actually logout when user is a guest (e.g. mid guest-login flow).
      // Prevents redirect to /auth/login when a 401 is wrongly handled as logout.
      if (authStore.isGuest || guestService.getGuestData()) {
        return;
      }
      
      isHandlingLogout = true;
      authStore.logout();
      uiStore.showToast('You have been logged out', 'info');
      
      // Note: The ProtectedRoute component will automatically redirect to login
      // when it detects isAuthenticated === false
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [authStore, uiStore]);

  const login = useCallback(async (credentials: LoginRequest) => {
    try {
      uiStore.setLoading(true, 'Logging in...');
      await authStore.login(credentials);
      uiStore.showToast('Login successful!', 'success');
    } catch (error) {
      const apiError = error as any;
      uiStore.setError(apiError, 'Login failed');
      
      // Show toast notification for login errors
      const errorMessage = apiError?.message || 'Login failed. Please check your credentials.';
      uiStore.showToast(errorMessage, 'error', 5000);
      
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [authStore, uiStore]);

  const register = useCallback(async (userData: RegisterRequest) => {
    try {
      uiStore.setLoading(true, 'Creating account...');
      await authStore.register(userData);
      uiStore.showToast('Account created successfully!', 'success');
    } catch (error) {
      const apiError = error as any;
      uiStore.setError(apiError, 'Registration failed');
      
      // Show toast notification for registration errors
      const errorMessage = apiError?.message || 'Registration failed. Please try again.';
      uiStore.showToast(errorMessage, 'error', 5000);
      
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [authStore, uiStore]);

  const logout = useCallback(async () => {
    try {
      uiStore.setLoading(true, 'Logging out...');
      await authStore.logout();
      uiStore.showToast('Logged out successfully', 'info');
    } catch (error) {
      uiStore.setError(error as any, 'Logout failed');
    } finally {
      uiStore.setLoading(false);
    }
  }, [authStore, uiStore]);

  const resetPassword = useCallback(async (data: ResetPasswordRequest) => {
    try {
      uiStore.setLoading(true, 'Resetting password...');
      await authStore.resetPassword(data);
      uiStore.showToast('Password reset successfully!', 'success');
    } catch (error) {
      const apiError = error as any;
      uiStore.setError(apiError, 'Password reset failed');
      
      // Show toast notification for password reset errors
      const errorMessage = apiError?.message || 'Password reset failed. Please try again.';
      uiStore.showToast(errorMessage, 'error', 5000);
      
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [authStore, uiStore]);

  const refreshAuth = useCallback(async () => {
    try {
      await authStore.refreshAuth();
    } catch (error) {
      console.error('Auth refresh failed:', error);
      authStore.logout();
      throw error;
    }
  }, [authStore]);

  const ensureValidToken = useCallback(async () => {
    try {
      return await authStore.ensureValidToken();
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }, [authStore]);

  return {
    // State
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated,
    isGuest: authStore.isGuest,
    isLoading: authStore.isLoading,
    error: authStore.error,
    isSessionRestored: authStore.isSessionRestored,
    
    // Actions
    login,
    register,
    logout,
    resetPassword,
    refreshAuth,
    ensureValidToken,
    clearError: authStore.clearError,
  };
};

export default useAuth;
